import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useGuardedNavigate } from "@/context/UnsavedChangesGuard";
import { AlertsBell } from "@/components/AlertsBell";
import { RandomAdminToast } from "@/components/RandomAdminToast";
import { AssistantWidget } from "@/components/AssistantWidget";
import { SquaresFourIcon as SquaresFour, UsersIcon as Users, FileTextIcon as FileText, PrinterIcon as Printer, ReceiptIcon as Receipt, FolderSimpleIcon as FolderSimple, UserGearIcon as UserGear, TicketIcon as Ticket, WrenchIcon as Wrench, GlobeIcon as Globe, ChartBarIcon as ChartBar, BookOpenIcon as BookOpen, GearIcon as Gear, SignOutIcon as SignOut, LifebuoyIcon as Lifebuoy, FunnelIcon as Funnel, ClipboardTextIcon as ClipboardText, FoldersIcon as Folders, CalendarBlankIcon as CalendarBlank, BriefcaseIcon as Briefcase, ListIcon as List, X, AlarmIcon as Alarm, TruckIcon as Truck, HandshakeIcon as Handshake, ChartLineUpIcon as ChartLineUp, CertificateIcon as Certificate, ToolboxIcon as Toolbox, WrenchIcon as WrenchIcon, ShieldCheckIcon as ShieldCheck, DesktopIcon as Desktop } from "@phosphor-icons/react";

const NAV = [
  { type: "item", to: "/", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard", end: true },
  { type: "item", to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clienti" },
  { type: "item", to: "/interventi", label: "Interventi", icon: CalendarBlank, testid: "nav-attivita" },
  { type: "item", to: "/lavorazioni", label: "Lavorazioni", icon: Toolbox, testid: "nav-lavorazioni" },
  { type: "item", to: "/helpdesk/ticket", label: "Helpdesk", icon: Ticket, testid: "nav-helpdesk" },
  {
    type: "section", key: "amministrazione", label: "Amministrazione", icon: Briefcase, testid: "nav-folder-amministrazione",
    children: [
      { to: "/contratti", label: "Contratti", icon: FileText, testid: "nav-contratti" },
      { to: "/documenti", label: "Documenti", icon: Folders, testid: "nav-documenti" },
      { to: "/fatturazione", label: "Fatturazione", icon: Receipt, testid: "nav-fatturazione" },
      { to: "/report", label: "Report", icon: FolderSimple, testid: "nav-report" },
    ],
  },
  {
    type: "section", key: "commerciale", label: "Commerciale", icon: Handshake, testid: "nav-folder-commerciale",
    children: [
      { to: "/commerciale/opportunita", label: "Opportunità", icon: Handshake, testid: "nav-opportunita" },
      { to: "/commerciale/preventivi", label: "Preventivi", icon: FileText, testid: "nav-preventivi" },
    ],
  },
  {
    type: "section", key: "servizi", label: "Servizi", icon: Globe, testid: "nav-folder-servizi",
    children: [
      { to: "/dispositivi", label: "Dispositivi", icon: Printer, testid: "nav-dispositivi" },
      { to: "/servizi", label: "Servizi", icon: Globe, testid: "nav-servizi", end: true },
    ],
  },
  {
    type: "section", key: "scadenziario", label: "Scadenziario", icon: Alarm, testid: "nav-folder-scadenziario",
    children: [
      { to: "/scadenziario/scadenze", label: "Scadenze", icon: Certificate, testid: "nav-scadenziario-scadenze" },
      { to: "/scadenziario/automezzi", label: "Automezzi", icon: Truck, testid: "nav-scadenziario-automezzi" },
    ],
  },
  { type: "item", to: "/riparazioni", label: "Riparazioni", icon: Wrench, testid: "nav-riparazioni" },
  {
    type: "section", key: "reportistica", label: "Reportistica", icon: ChartBar, testid: "nav-folder-reportistica",
    children: [
      { to: "/reportistica", label: "Assistenza Tecnica", icon: Lifebuoy, testid: "nav-reportistica", end: true },
      { to: "/reportistica/servizi", label: "Servizi in vendita", icon: Globe, testid: "nav-reportistica-servizi" },
      { to: "/reportistica/sistemisti", label: "Sistemisti", icon: Toolbox, testid: "nav-reportistica-sistemisti" },
      { to: "/commerciale/statistiche", label: "Commerciale", icon: Handshake, testid: "nav-reportistica-commerciale" },
    ],
  },
  {
    type: "section", key: "knowledge", label: "Knowledge Base", icon: BookOpen, testid: "nav-knowledge",
    children: [
      { to: "/knowledge-base", label: "Knowledge Base", icon: BookOpen, testid: "nav-knowledge-home", end: true },
      { to: "/knowledge/catalogo-attivita", label: "Catalogo Attività", icon: ClipboardText, testid: "nav-catalogo-attivita" },
    ],
  },
  {
    type: "section", key: "tools", label: "Tools", icon: Toolbox, testid: "nav-folder-tools",
    children: [
      { to: "/tools/nis2", label: "NIS2", icon: ShieldCheck, testid: "nav-nis2" },
      { to: "/tools/iso27001", label: "ISO 27001", icon: Certificate, testid: "nav-iso27001" },
    ],
  },
  { type: "item", to: "/utenti", label: "Utenti e Tecnici", icon: UserGear, testid: "nav-utenti", adminOnly: true },
  { type: "item", to: "/log", label: "Log", icon: ClipboardText, testid: "nav-log", adminOnly: true },
  { type: "item", to: "/impostazioni", label: "Impostazioni", icon: Gear, testid: "nav-impostazioni", adminOnly: true },
];

export function Footer() {
  return (
    <div className="text-center text-xs text-muted-foreground py-6 border-t border-border mt-10 leading-relaxed px-4">
      Tecnologie S.r.l. &mdash; Viale dell'Industria, 11 &mdash; 37023 Grezzana (VR)
      <br className="hidden sm:block" />
      <span className="sm:ml-1">assistenza@tecnologieverona.it &mdash; +39 045-4770241</span>
    </div>
  );
}

const isChildActive = (children, pathname) =>
  children.some((c) => (c.end ? pathname === c.to : pathname === c.to || pathname.startsWith(c.to + "/")));

function SectionSubmenu({ section, collapsed }) {
  const guardedNavigate = useGuardedNavigate();
  return (
    <aside
      data-testid={`section-submenu-${section.key}`}
      className="w-full md:w-52 md:shrink-0 border-b md:border-b-0 md:border-r border-border bg-secondary/20 px-3 py-2 md:py-6"
    >
      <div className="hidden md:block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold px-3 mb-3">{section.label}</div>
      <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
        {section.children.map((c) => {
          const CIcon = c.icon;
          return (
            <NavLink key={c.to} to={c.to} end={c.end} data-testid={`submenu-${c.testid}`}
              onClick={(e) => { if (guardedNavigate) { e.preventDefault(); guardedNavigate(c.to); } }}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm whitespace-nowrap transition-colors shrink-0 ${
                  isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }>
              <CIcon size={16} className="shrink-0" /> {c.label}
            </NavLink>
          );
        })}
      </div>
    </aside>
  );
}

export function InternalLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const guardedNavigate = useGuardedNavigate();
  const location = useLocation();
  const handleLogout = async () => { await logout(); navigate("/login"); };

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { localStorage.setItem("sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  // Chiudi il drawer mobile al cambio pagina
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const visibleNav = NAV.filter((n) => !n.adminOnly || user?.role === "admin");
  const activeSection = NAV.find((n) => n.type === "section" && isChildActive(n.children, location.pathname));

  const itemClass = (isActive) =>
    `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
      isActive ? "border-primary bg-secondary text-primary font-semibold" : "border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground"
    } ${collapsed ? "justify-center px-0" : ""}`;

  return (
    <div className="min-h-screen flex bg-background">
      <RandomAdminToast />
      <AssistantWidget />
      {/* Overlay mobile */}
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-overlay" />}

      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "1" : "0"}
        className={`fixed top-0 left-0 h-screen border-r border-border bg-white flex flex-col z-40 transition-all duration-200
          ${collapsed ? "w-16" : "w-64"} ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className={`flex items-center border-b border-border ${collapsed ? "justify-center px-2 py-4" : "justify-between px-4 py-4"}`}>
          {!collapsed && (
            <div className="min-w-0">
              <img src="/brand/logo.png" alt="Tecnologie" className="h-7 w-auto object-contain" />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1.5 font-semibold">TECSuite</div>
            </div>
          )}
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed((c) => !c)}
            className="hidden md:flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors shrink-0"
            title={collapsed ? "Espandi menu" : "Riduci menu"}
          >
            <List size={18} weight="bold" />
          </button>
          <button
            data-testid="sidebar-close-mobile"
            onClick={() => setMobileOpen(false)}
            className="md:hidden h-8 w-8 flex items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary shrink-0"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
          {visibleNav.map((n) => {
            if (n.type === "item") {
              const Icon = n.icon;
              return (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} title={collapsed ? n.label : undefined}
                  onClick={(e) => { if (guardedNavigate) { e.preventDefault(); guardedNavigate(n.to); } }}
                  className={({ isActive }) => itemClass(isActive)}>
                  <Icon size={18} weight="bold" className="shrink-0" />
                  {!collapsed && <span className="truncate">{n.label}</span>}
                </NavLink>
              );
            }
            // section: voce macro singola, nessuna espansione nel menu principale
            const Icon = n.icon;
            const active = isChildActive(n.children, location.pathname);
            return (
              <button key={n.key}
                data-testid={n.testid}
                onClick={() => (guardedNavigate ? guardedNavigate(n.children[0].to) : navigate(n.children[0].to))}
                title={collapsed ? n.label : undefined}
                className={`w-full flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  active ? "border-primary bg-secondary text-primary font-semibold" : "border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground"
                } ${collapsed ? "justify-center px-0" : ""}`}
              >
                <Icon size={18} weight="bold" className="shrink-0" />
                {!collapsed && <span className="truncate flex-1 text-left">{n.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className={`border-t border-border ${collapsed ? "p-2" : "p-4"}`}>
          {!collapsed && (
            <>
              <div className="text-sm font-medium text-foreground truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate mb-3">{user?.email}</div>
            </>
          )}
          <button data-testid="logout-btn" onClick={handleLogout} title="Esci"
            className={`flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors ${collapsed ? "justify-center w-full" : ""}`}>
            <SignOut size={16} weight="bold" /> {!collapsed && "Esci"}
          </button>
        </div>
      </aside>

      <main className={`flex-1 min-w-0 min-h-screen flex flex-col transition-all duration-200 ${collapsed ? "md:ml-16" : "md:ml-64"}`}>
        <div className="h-14 border-b border-border bg-white/80 backdrop-blur-sm flex items-center justify-between px-4 md:px-8 sticky top-0 z-20">
          <button data-testid="sidebar-open-mobile" onClick={() => setMobileOpen(true)}
            className="md:hidden h-9 w-9 flex items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary">
            <List size={20} weight="bold" />
          </button>
          <div className="flex-1" />
          <AlertsBell />
        </div>
        <div className="flex-1 flex flex-col md:flex-row min-h-0 min-w-0">
          {activeSection && <SectionSubmenu section={activeSection} collapsed={collapsed} />}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex-1 min-w-0">{children}</div>
            <Footer />
          </div>
        </div>
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-8">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
